import React, { useEffect } from 'react';
import { Container, Row, Col, Form, Badge } from 'react-bootstrap';
import { CatalogItem } from '../../../managers/CatalogManager';

interface Props {
  catalogItems: CatalogItem[];
  selectedCatalogItemId: string;
  selectedVariantId: string;
  onSelectItem: (itemId: string) => void;
  onSelectVariant: (variantId: string) => void;
  excludeCatalogItemId?: string;
  excludeCatalogItemVariantId?: string;
}

const CatalogItemSelector: React.FC<Props> = ({
  catalogItems,
  selectedCatalogItemId,
  selectedVariantId,
  onSelectItem,
  onSelectVariant,
  excludeCatalogItemId,
  excludeCatalogItemVariantId
}) => {
  const filteredItems = catalogItems
    .map(item => {
      const filteredVariants = (item.variants || [])
        .filter(v => {
          // Exclude discontinued variants
          if (v?.status?.toUpperCase() === 'DISCONTINUED') return false;

          // Exclude current variant
          const shouldExclude = item.id === excludeCatalogItemId && v.id === excludeCatalogItemVariantId;
          return !shouldExclude;
        });
      return { ...item, variants: filteredVariants };
    })
    .filter(item => {
      if (item.id !== excludeCatalogItemId) return true;
      // For same product type, only include if there are remaining active variants after filtering
      return item.variants.length > 0;
    });

  // Auto-select the only variant when the selected item has exactly one variant
  useEffect(() => {
    const selectedItem = filteredItems.find(i => i.id === selectedCatalogItemId);
    const variants = selectedItem?.variants ?? [];

    if (selectedItem && variants.length === 1) {
      const onlyId = variants[0]?.id;
      if (onlyId) {
        onSelectVariant(onlyId);
      }
    }
  }, [filteredItems, selectedCatalogItemId, onSelectVariant]);

  return (
    <>
      <Container fluid>
        <Row className="gx-1 gy-2 gx-sm-3 gy-sm-4">
          {filteredItems.map((item) => {
            const isSelected = item.id === selectedCatalogItemId;
            const variants = item.variants ?? [];
            const activeVariants = variants.filter(v => v?.status?.toUpperCase() !== 'DISCONTINUED');
            const hasMultipleVariants = activeVariants.length > 1;
            const hasSingleVariant = activeVariants.length === 1;

            // Use item-level image by default; swap in selected variant's image when one is chosen
            let previewImg = item.previewImageUrls?.[0];
            if (isSelected && selectedVariantId) {
              const selectedVariant = variants.find(v => v.id === selectedVariantId);
              previewImg = selectedVariant?.previewImageUrls?.[0] || previewImg;
            }

            return (
              <Col key={item.id} xs="6" sm="6" md="4" lg="4" className="mb-3">
                <div
                  onClick={() => onSelectItem(item.id)}
                  className={`border rounded p-1 text-center h-100 ${isSelected ? 'border-primary' : 'border-light'}`}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  {/* Early Access Badge */}
                  {item.accessRoles && item.accessRoles.length > 0 && (
                    <Badge
                      bg="warning"
                      text="dark"
                      className="position-absolute top-0 start-0 m-2"
                      style={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        padding: '0.35rem 0.5rem',
                        zIndex: 10
                      }}
                    >
                      Early Access
                    </Badge>
                  )}

                  {previewImg && (
                    <img
                      src={previewImg}
                      alt={item.name}
                      className="img-fluid mb-1 rounded"
                      style={{ maxHeight: '250px', objectFit: 'contain' }}
                    />
                  )}
                  <div className={isSelected ? 'text-primary fw-bold' : 'text-muted'}>
                    {item.name}
                  </div>

                  {/* Variant selection logic */}
                  {isSelected && (
                    <>
                      {hasMultipleVariants && (
                        <Form.Group className="mt-1">
                          <Form.Label className="visually-hidden">Variant</Form.Label>
                          <Form.Select
                            name="catalogItemVariantId"
                            value={selectedVariantId}
                            required
                            onChange={(e) => onSelectVariant(e.target.value)}
                          >
                            <option value="">Select a size</option>
                            {activeVariants.map((variant) => (
                              <option key={variant.id} value={variant.id}>
                                {variant.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}

                      {hasSingleVariant && (
                        <>
                          {/* Show variant name for single variants */}
                          <div className="mt-1 text-muted small">
                            {activeVariants[0].name}
                          </div>
                          {/* Hidden input ensures form submission includes the variant */}
                          <Form.Control
                            type="hidden"
                            name="catalogItemVariantId"
                            value={activeVariants[0].id}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </Container>
    </>
  );
};

export default CatalogItemSelector;
